import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserContext } from "../context/UserContext";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// ✅ Reuse styled components from PostCodeIssue
// (You can move common styled components into a shared file later)
const Container = styled.div`
  min-height: 100vh;
  background-color: #0d0d0d;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem;
  gap: 2rem;
  color: white;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    padding: 1rem;
  }
`;

const FormWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Card = styled.div`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 500px;
  width: 100%;
  color: white;
`;

const Avatar = styled(FaUserCircle)`
  font-size: 40px;
  color: #555;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Loader = styled.div`
  border: 6px solid #f3f3f3;
  border-top: 6px solid #a855f7;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Form = styled.form`
  background-color: #1a1a1a;
  padding: 20px;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #2a2a2a;
  color: #fff;
`;

const Textarea = styled.textarea`
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #2a2a2a;
  color: #fff;
  min-height: 120px;
`;

const CodeEditor = styled.textarea`
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #2e2e3e;
  color: #fff;
  font-family: "Fira Code", monospace;
  font-size: 14px;
  min-height: 180px;
`;

const Select = styled.select`
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #2a2a2a;
  color: #fff;
`;

const Button = styled.button`
  background: #a855f7;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background: #9333ea;
  }
`;

const Heading = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  color: #a855f7;
  margin-bottom: 1.5rem;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  background: #2a2a2a;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
`;

const FileName = styled.span`
  color: #e5e7eb;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #f87171;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    color: #ef4444;
  }
`;

const EditCodeIssue = () => {
  const { id } = useParams(); // ✅ Get issue ID from URL
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // States
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState("free");
  const [technology, setTechnology] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [existingScreenshot, setExistingScreenshot] = useState(null);

  const storage = getStorage();

  // ✅ Load existing issue
  useEffect(() => {
    const fetchIssue = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "codeIssues", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title);
          setDescription(data.description);
          setCode(data.code);
          setType(data.type);
          setTechnology(data.technology);
          setExistingScreenshot(data.screenshot || null);
        } else {
          toast.error("Issue not found");
          navigate("/code-issues");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load issue");
      } finally {
        setLoading(false);
      }
    };

    fetchIssue();
  }, [id, navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setScreenshot(file);
    } else {
      toast.error("Only image files are allowed!");
      e.target.value = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let screenshotURL = existingScreenshot;

      // If a new screenshot is uploaded
      if (screenshot) {
        const storageRef = ref(
          storage,
          `screenshots/${Date.now()}-${screenshot.name}`
        );
        await uploadBytes(storageRef, screenshot);
        screenshotURL = await getDownloadURL(storageRef);
      }

      const docRef = doc(db, "codeIssues", id);
      await updateDoc(docRef, {
        title,
        description,
        code,
        type,
        technology,
        screenshot: screenshotURL,
        updatedAt: Timestamp.now(),
        updatedBy: user?.name || "Anonymous",
      });

      toast.success("Issue updated successfully!");
      navigate("/code-issues");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update issue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {loading && (
        <Overlay>
          <Loader />
        </Overlay>
      )}

      <FormWrapper>
        <Form onSubmit={handleSubmit}>
          <Heading>Edit Code Issue</Heading>

          <label>Issue Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <label>Description</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />

          <label>Code</label>
          <CodeEditor value={code} onChange={(e) => setCode(e.target.value)} />

          <label>Type</label>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </Select>

          <label>Technologies</label>
          <Select
            value={technology}
            onChange={(e) => setTechnology(e.target.value)}
            required
          >
            {/* <option value="">-- Select Technology --</option> */}
            <option value="react">React</option>
            <option value="node">Node.js</option>
            <option value="firebase">Firebase</option>
            {/* Add more as needed */}
          </Select>

          <label>Update Screenshot</label>
          <Input type="file" accept="image/*" onChange={handleFileChange} />

          {(screenshot || existingScreenshot) && (
            <FilePreview>
              <FileName>
                {screenshot ? screenshot.name : "Existing Screenshot"}
              </FileName>
              <CloseButton
                onClick={() => {
                  setScreenshot(null);
                  setExistingScreenshot(null);
                }}
              >
                ✕
              </CloseButton>
            </FilePreview>
          )}

          <Button type="submit">Update Issue</Button>
        </Form>
      </FormWrapper>
    </Container>
  );
};

export default EditCodeIssue;
